import React from 'react';

import { Box, Button, Grid } from '@mui/material';
import { compact } from 'es-toolkit';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';

import { DeepPartial } from '@hrlogics/helper-types';
import { Field, Form, FormApi, FormSpy } from '@hrlogics/react-components/form';

import { appRoutes, DateFormatPatterns, RouterEntitiesId, SSN_MASK } from '@clearverify/constants';

import {
  InboxItemsScrollableListDocument,
  useCreateTestVerificationRequestMutation,
} from 'src/api';
import { DateField, MaskedTextField, TextField } from 'src/components/finalForm';
import {
  VerificationRequestCreateByVerifierModalFormConsumerErrorBox,
  VerificationRequestCreateByVerifierModalFormErrorBox,
  VerificationRequestCreateFormVerificationReportTypeField,
  VerificationRequestCreateModalFormEmployerField,
} from 'src/components/verificationRequest';
import {
  cvStaffTestVerificationsRequestTypeOptions,
  VerificationRequestCreateFormFields,
} from 'src/constants/verificationRequest';
import { useNotification } from 'src/hooks/app';
import { useInboxItemsQueryVariables } from 'src/hooks/inboxItem';
import { useVerificationRequestFormData } from 'src/hooks/verificationRequest';
import { convertEmployerListItemsToEmployerDataItems } from 'src/utils/employer';
import { buildUrl } from 'src/utils/router';
import { validateWithYupSchema } from 'src/utils/yup';

import { TestVerificationRequestCreateFormValues } from './types';
import { testVerificationRequestCreateValidationSchema } from './utils';

interface TestVerificationRequestCreateModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const TestVerificationRequestCreateModalForm: React.FC<
  TestVerificationRequestCreateModalFormProps
> = ({ closeModal, switchClosability }) => {
  const navigate = useNavigate();

  const { createApolloContext } = useNotification();

  const { isFormInfoLoading, resetData, startSubmitting, stopSubmitting } =
    useVerificationRequestFormData();

  const inboxItemsQueryVariables = useInboxItemsQueryVariables();

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset data on unmount only
  React.useEffect(() => {
    return () => resetData();
  }, []);

  const [createTestVerificationRequest] = useCreateTestVerificationRequestMutation({
    context: createApolloContext({
      errorMessage: 'Error creating test verification request',
      successMessage: 'Test verification request successfully created',
    }),
    refetchQueries: [
      { query: InboxItemsScrollableListDocument, variables: inboxItemsQueryVariables },
    ],
  });

  const minDate = React.useMemo(() => DateTime.fromISO('1800-01-01'), []);
  const maxDate = React.useRef(DateTime.now());

  const onSubmit = React.useCallback(
    async (
      formValues: TestVerificationRequestCreateFormValues,
      formApi: FormApi<
        TestVerificationRequestCreateFormValues,
        DeepPartial<TestVerificationRequestCreateFormValues>
      >,
    ) => {
      formApi.change(VerificationRequestCreateFormFields.error, null);

      const employeeDateOfBirth = DateTime.fromISO(formValues.employeeDateOfBirth);
      if (!employeeDateOfBirth.isValid) return;

      startSubmitting();
      switchClosability(false);

      let success = false;
      let inboxItemIdToRedirect: string | null = null;

      try {
        const { data } = await createTestVerificationRequest({
          variables: {
            ssn: formValues.ssn,
            employeeFirstName: formValues.employeeFirstName,
            employeeLastName: formValues.employeeLastName,
            employeeBirthDate: employeeDateOfBirth.toISODate(),
            requestType: compact(formValues.verificationReportType),
            companies: convertEmployerListItemsToEmployerDataItems(formValues.employers),
          },
        });
        success = true;

        if (data?.createTestVerificationRequest.inboxItemId) {
          inboxItemIdToRedirect = data.createTestVerificationRequest.inboxItemId;
        }

        switchClosability(true);
      } finally {
        stopSubmitting();
        switchClosability(true);
        if (success) {
          closeModal();
        }
      }

      if (inboxItemIdToRedirect) {
        navigate(
          buildUrl(appRoutes.inbox.inboxTestRequestItem, {
            [RouterEntitiesId.inboxItem]: inboxItemIdToRedirect,
          }),
        );
      }
    },
    [
      closeModal,
      createTestVerificationRequest,
      startSubmitting,
      stopSubmitting,
      navigate,
      switchClosability,
    ],
  );

  return (
    <Form<TestVerificationRequestCreateFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      subscription={{ valid: true, submitting: true }}
      containerProps={{ mt: 0 }}
      validate={validateWithYupSchema(testVerificationRequestCreateValidationSchema)}
    >
      {({ submitting, valid }) => {
        return (
          <React.Fragment>
            <Grid container spacing={2.5}>
              <Grid size={12}>
                <Field
                  name={VerificationRequestCreateFormFields.ssn}
                  component={MaskedTextField}
                  label="SSN"
                  mask={SSN_MASK}
                  fullWidth
                  required
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeFirstName}
                  component={TextField}
                  label="Employee First Name"
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeLastName}
                  component={TextField}
                  label="Employee Last Name"
                  fullWidth
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <Field
                  name={VerificationRequestCreateFormFields.employeeDateOfBirth}
                  component={DateField}
                  minDate={minDate}
                  maxDate={maxDate.current}
                  inputFormat={DateFormatPatterns.shortDateWithSlash}
                  defaultValue={DateFormatPatterns.shortDateWithSlash}
                  label="Employee Date of Birth"
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <FormSpy subscription={{ values: true, errors: true }}>
                  {() => <VerificationRequestCreateModalFormEmployerField showNonLiveEmployers />}
                </FormSpy>
              </Grid>

              <Grid size={12}>
                <VerificationRequestCreateFormVerificationReportTypeField
                  options={cvStaffTestVerificationsRequestTypeOptions}
                  multiple
                  disableWhenIncomeUnavailable={false}
                />
              </Grid>
            </Grid>

            <VerificationRequestCreateByVerifierModalFormErrorBox />

            <FormSpy subscription={{ values: true, errors: true }}>
              {() => <VerificationRequestCreateByVerifierModalFormConsumerErrorBox />}
            </FormSpy>

            <Box display="flex" gap={2} mt={4} justifyContent="center">
              <Button
                variant="outlined"
                color="error"
                onClick={() => closeModal()}
                disabled={submitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting}
                loading={submitting || isFormInfoLoading}
              >
                Submit request
              </Button>
            </Box>
          </React.Fragment>
        );
      }}
    </Form>
  );
};

const initialValues: Partial<TestVerificationRequestCreateFormValues> = {
  [VerificationRequestCreateFormFields.verificationReportType]: [
    cvStaffTestVerificationsRequestTypeOptions.at(2)?.value,
  ],
};
